import { InterpolationPrimitive } from '@emotion/serialize'
import { Button } from 'components/button/Button'
import { useConfettiGun } from 'components/directive/directiveConfetti/DirectiveConfetti'
import { DirectiveProgress } from 'components/directive/directiveProgress/DirectiveProgress'
import {
  DirectiveStepsNav,
  TDirectiveStep,
} from 'components/directive/directiveStepsNav/DirectiveStepsNav'
import { DirectiveFullSpec } from 'components/directive/utils/directiveSpecs'
import { getNextStepUrl } from 'components/directive/utils/directiveSpecs/getNextStepUrl'
import {
  calculateDirectiveProgress,
  getDirectivePageName,
  scrollToFirstFormError,
  useEncouragementText,
} from 'components/directive/utils/directiveUtils'
import { Layout } from 'components/layout/Layout'
import { useUpsellModalOnExit } from 'components/modal/upsellModal/utils/hooks'
import { PageTitle } from 'components/PageTitle'
import { Spacer } from 'components/spacer/Spacer'
import { SupportSideBar } from 'components/supportSideBar/SupportSideBar'
import { useTranslation } from 'next-i18next'
import Router, { useRouter } from 'next/router'
import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { UseFormReturn } from 'react-hook-form'
import { getRandomInteger, toUpperCase } from 'utils/general'
import { useLoadingCallback, useRunOnlyOnChange, useTimeout, useWindowSize } from 'utils/hooks'

import {
  directiveContentHeaderCss,
  SButtonsContainer,
  SDirectiveContainer,
  SDirectiveContent,
  SHeader,
  SHeaderDashboard,
  SMobileImageContainer,
  SRightPanel,
  SSafeForm,
} from './DirectiveFormLayout.styled'

export type SubmitType = 'save' | 'submit'

export interface DirectiveFormProps {
  directiveSpec: DirectiveFullSpec
  dataTestIdPrefix: string
  steps: TDirectiveStep[]
  title: string
  image?: ReactElement
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>
  onSubmit?: (type: SubmitType, beforeExit?: () => Promise<boolean>) => Promise<void>
  children: ReactNode
  header?: ReactElement
  actionButtons?: ReactNode
  hideBack?: boolean
  isReadOnly?: boolean
  isLoading?: boolean
  disableSideNavigation?: boolean
  hideSideNavigationSteps?: boolean
  noExit?: boolean
  saveAndContinueText?: string
  simple?: boolean
  titleCss?: InterpolationPrimitive
}

/**
 * Property used by PersonInput to determine on outer click behavior.
 */
export const directiveFormLayoutDataPropertySubmitButtonType = 'data-directive-submit-button-type'

export type DirectiveFormContextValue = {
  isReadOnly: boolean
  simple: boolean
  hasClickedSaveExitRecently?: () => boolean
}
export const DirectiveFormContext = createContext<DirectiveFormContextValue>({
  isReadOnly: false,
  simple: false,
})
export const useDirectiveFormContext = () => useContext(DirectiveFormContext)

export function DirectiveFormLayout(props: DirectiveFormProps) {
  const { t } = useTranslation('directive')
  const { t: tCommon } = useTranslation()
  const router = useRouter()
  const pageName = getDirectivePageName(router)
  const { isLarge, isTablet, isDesktop } = useWindowSize()

  const progressPlacement = isLarge ? 'steps-nav' : 'header-actions'

  const confettiGun = useConfettiGun()

  const [considerTutorialDone, setConsiderTutorialDone] = useState(() => {
    const noStepIsDone = !props.steps.some((step) => step.isDone)
    const isFirstPage = router.pathname.match(/about-you$/)

    // we only want the delay when the user comes to the page and hasn't completed any steps
    const considerDoneAfterDelay = noStepIsDone && isFirstPage
    const considerDoneImmediately = !considerDoneAfterDelay
    return considerDoneImmediately
  })
  const progress = props.isLoading
    ? undefined
    : calculateDirectiveProgress(props.steps, considerTutorialDone)

  useTimeout(() => {
    if (!considerTutorialDone) {
      setConsiderTutorialDone(true)
    }
  }, 400)

  useRunOnlyOnChange(progress, (prev, next) => {
    if (prev == null || next == null) return
    if (prev < next) {
      // we don't want to shoot confetti when the user makes a validation error
      // we should only shoot when the progress increases
      const particleCount = next === 1 ? 300 : getRandomInteger(40, 70)
      if (progressPlacement === 'header-actions') {
        confettiGun.shoot({
          spread: 60,
          origin: {
            y: 1,
            x: 0.5,
          },
          particleCount,
        })
      } else {
        confettiGun.shoot({
          angle: 60,
          spread: 90,
          particleCount,
        })
      }
    }
  })
  const encouragementText = useEncouragementText(String(progress))
  const upsellBeforeExitEnabled = !props.simple

  const [submit, isLoading] = useLoadingCallback(async (type: SubmitType) => {
    await props.onSubmit?.(type, upsellBeforeExit)
    if (type === 'submit') scrollToFirstFormError(props.form)
  })

  const { modal: upsellModalOnExit, beforeExit: useUpsellModalOnExit_beforeExit } =
    useUpsellModalOnExit(
      props.directiveSpec.analyticsDirectiveType,
      props.form.formState,
      props.onSubmit
    )
  const upsellBeforeExit = upsellBeforeExitEnabled ? useUpsellModalOnExit_beforeExit : undefined

  const saveExitClickedRecently = useRef(false)
  const onSaveExitClick = () => {
    saveExitClickedRecently.current = true
    setTimeout(() => {
      saveExitClickedRecently.current = false
    }, 3000)
  }
  const hasClickedSaveExitRecently = useCallback(() => saveExitClickedRecently.current, [])

  const ctxValue = useMemo(
    (): DirectiveFormContextValue => ({
      isReadOnly: !!props.isReadOnly,
      hasClickedSaveExitRecently,
      simple: !!props.simple,
    }),
    [props.isReadOnly, hasClickedSaveExitRecently, props.simple]
  )

  const readOnlyDirectiveNextButtonAction = () => {
    const analyticsDirectiveType = props.directiveSpec.analyticsDirectiveType
    const directiveName =
      analyticsDirectiveType === 'poa' ? 'POWER_OF_ATTORNEY' : toUpperCase(analyticsDirectiveType)
    const nextStep = getNextStepUrl(router, directiveName)
    return Router.push(nextStep)
  }

  const directiveContent = (
    <SDirectiveContent
      hideBack={props.hideBack}
      actions={
        progressPlacement === 'header-actions' && (
          <DirectiveProgress order={['progress-bar', 'text']} progress={progress ?? 0} />
        )
      }
      headerCss={directiveContentHeaderCss}
    >
      <SHeader css={props.titleCss}>{props.title}</SHeader>
      <SMobileImageContainer>{props.image}</SMobileImageContainer>
      <SSafeForm noValidate formState={props.form.formState} onSubmit={(e) => e.preventDefault()}>
        {props.children}
        <Spacer size={['2rem', '2rem', 0]} />
        <SButtonsContainer>
          {props.isReadOnly ? (
            <Button
              type="button"
              loading={isLoading}
              size="large"
              onClick={readOnlyDirectiveNextButtonAction}
            >
              {tCommon('common.next')}
            </Button>
          ) : props.actionButtons ? (
            props.actionButtons
          ) : (
            <>
              {!props.noExit && (
                <Button
                  loading={isLoading}
                  size="large"
                  variant="secondary"
                  data-testid={`${props.dataTestIdPrefix}-${pageName}-save-exit`}
                  {...{ [directiveFormLayoutDataPropertySubmitButtonType]: 'save' }}
                  data-directive-submit-button-type={'save'}
                  onClick={async () => {
                    onSaveExitClick()
                    await submit('save')
                  }}
                  type="button"
                >
                  {t('directive.buttons.save_and_exit')}
                </Button>
              )}

              <Button
                loading={isLoading}
                size="large"
                data-testid={`${props.dataTestIdPrefix}-${pageName}-save-continue`}
                {...{ [directiveFormLayoutDataPropertySubmitButtonType]: 'submit' }}
                data-directive-submit-button-type={'submit'}
                onClick={async () => await submit('submit')}
                type="button"
              >
                {props.saveAndContinueText ?? t('directive.buttons.save_and_continue')}
              </Button>
            </>
          )}
        </SButtonsContainer>
      </SSafeForm>
    </SDirectiveContent>
  )

  return (
    <DirectiveFormContext.Provider value={ctxValue}>
      <Layout
        header={
          props.header ?? (
            <SHeaderDashboard
              beforeExit={upsellBeforeExit}
              showLogo
              showMenu={false}
              buttonVariant={props.simple ? 'help' : 'upgrade'}
            />
          )
        }
      >
        <PageTitle
          title={t('directive.common.meta_title', {
            directive: props.directiveSpec.useTranslatedNames().short,
          })}
        />

        {upsellModalOnExit}
        <SDirectiveContainer>
          <div>
            <DirectiveStepsNav
              dataTestIdPrefix={props.dataTestIdPrefix}
              steps={props.steps}
              disabled={props.disableSideNavigation}
              hideSteps={props.hideSideNavigationSteps}
              progress={
                progressPlacement === 'steps-nav' && (
                  <>
                    <DirectiveProgress
                      order={['text', 'progress-bar']}
                      progress={progress ?? 0}
                      encouragementText={progress && progress > 0 ? encouragementText : undefined}
                    />
                    <div css={{ display: 'flex', justifyContent: 'center' }}>
                      {confettiGun.origin}
                    </div>
                  </>
                )
              }
            />
            {!isDesktop && <SupportSideBar />}
            {isTablet && directiveContent}
          </div>
          {!isTablet && directiveContent}
          <div>
            <SRightPanel>{<SupportSideBar />}</SRightPanel>
          </div>
        </SDirectiveContainer>
      </Layout>
    </DirectiveFormContext.Provider>
  )
}
